package com.kinoshazam.screens.details

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.foundation.focusable
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.Velocity
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import com.kinoshazam.uiKit.components.image.ImageUiKitState
import com.kinoshazam.uiKit.components.image.UiKitImage
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.OnBackPressed
import com.kinoshazam.uiKit.trailer.TrailerControls
import com.kinoshazam.uiKit.trailer.TrailerPlayer
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.film
import kinoshazam.resources.generated.resources.trailer
import kotlin.math.roundToInt
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

val DetailsHeroHeight = 310.dp

class TrailerExpansionState internal constructor(
    private val scope: CoroutineScope,
) {
    var progress by mutableFloatStateOf(0f)
        private set

    private val anim = Animatable(0f)
    private var job: Job? = null
    var audible by mutableStateOf(false)
        private set

    val muted: Boolean get() = !audible

    fun expand() {
        audible = true
        TrailerControls.setAudible(true)
        animateTo(1f)
    }

    fun collapse() {
        audible = false
        TrailerControls.setAudible(false)
        animateTo(0f)
    }

    fun toggle() {
        if (progress > 0.5f) collapse() else expand()
    }

    private fun animateTo(target: Float) {
        job?.cancel()
        job = scope.launch {
            anim.snapTo(progress)
            anim.animateTo(target, tween(520, easing = FastOutSlowInEasing)) {
                progress = value
            }
        }
    }
}

@Composable
fun rememberTrailerExpansion(): TrailerExpansionState {
    val scope = rememberCoroutineScope()
    return remember { TrailerExpansionState(scope) }
}

private const val TrailerRevealDelayMs = 4_000

@Composable
fun rememberTrailerCovered(trailerUrl: String?): Boolean {
    val active = !trailerUrl.isNullOrBlank()
    var playing by remember(trailerUrl) { mutableStateOf(false) }
    var revealed by remember(trailerUrl) { mutableStateOf(false) }
    SideEffect {
        TrailerControls.onPlaying = if (active) {
            { playing = true }
        } else {
            null
        }
    }
    DisposableEffect(trailerUrl) {
        onDispose { TrailerControls.onPlaying = null }
    }
    LaunchedEffect(trailerUrl, playing) {
        if (!playing) return@LaunchedEffect
        delay(TrailerRevealDelayMs.toLong())
        revealed = true
    }
    return active && !revealed
}

@Composable
fun rememberTrailerPull(
    enabled: Boolean,
    atTop: () -> Boolean,
    onPull: () -> Unit,
): NestedScrollConnection {
    val threshold = with(LocalDensity.current) { 96.dp.toPx() }
    val enabledState = rememberUpdatedState(enabled)
    val atTopState = rememberUpdatedState(atTop)
    val onPullState = rememberUpdatedState(onPull)
    return remember(threshold) {
        object : NestedScrollConnection {
            var pull = 0f
            override fun onPostScroll(
                consumed: Offset,
                available: Offset,
                source: NestedScrollSource,
            ): Offset {
                if (!enabledState.value || source != NestedScrollSource.UserInput) return Offset.Zero
                if (!atTopState.value()) {
                    pull = 0f
                    return Offset.Zero
                }
                if (available.y > 0f) {
                    pull += available.y
                    if (pull >= threshold) {
                        pull = 0f
                        onPullState.value()
                    }
                } else {
                    pull = 0f
                }
                return Offset.Zero
            }

            override suspend fun onPreFling(available: Velocity): Velocity {
                pull = 0f
                return Velocity.Zero
            }
        }
    }
}

fun LayoutCoordinates.rectInside(ancestor: LayoutCoordinates): Rect? {
    if (!isAttached || !ancestor.isAttached) return null
    val origin = ancestor.localPositionOf(this, Offset.Zero)
    return Rect(
        left = origin.x,
        top = origin.y,
        right = origin.x + size.width,
        bottom = origin.y + size.height,
    )
}

@Composable
fun BannerContainer(
    imageUrl: String?,
    showPoster: Boolean,
    onPlaced: (LayoutCoordinates) -> Unit,
    modifier: Modifier = Modifier,
) {
    val fade = Brush.verticalGradient(
        colors = listOf(
            UiKitTheme.colors.color_3.copy(alpha = 0.3f),
            UiKitTheme.colors.color_3,
        ),
    )
    Box(
        modifier
            .fillMaxWidth()
            .height(DetailsHeroHeight)
            .onGloballyPositioned(onPlaced),
    ) {
        if (showPoster) {
            UiKitImage(
                state = ImageUiKitState(
                    url = imageUrl,
                    contentScale = ContentScale.Crop,
                ),
                modifier = Modifier.fillMaxSize(),
            )
        } else {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .drawWithContent {
                        drawRect(color = Color.Black, blendMode = BlendMode.Clear)
                    },
            )
        }
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(brush = fade),
        )
    }
}

fun syncTrailerCaption(
    caption: LayoutCoordinates,
    ancestor: LayoutCoordinates,
    slot: Rect,
    density: Density,
) {
    val rect = caption.rectInside(ancestor) ?: return
    val chrome = TrailerControls.banner
    chrome.overlayTopPx = rect.top - slot.top
    chrome.overlayLeftPx = rect.left - slot.left
    chrome.overlayWidthPx = rect.width
    with(density) {
        chrome.overlayTopDp = chrome.overlayTopPx.toDp().value
        chrome.overlayLeftDp = chrome.overlayLeftPx.toDp().value
        chrome.overlayWidthDp = chrome.overlayWidthPx.toDp().value
    }
}

fun syncTrailerActions(
    actions: LayoutCoordinates,
    ancestor: LayoutCoordinates,
    slot: Rect,
    density: Density,
) {
    val rect = actions.rectInside(ancestor) ?: return
    val chrome = TrailerControls.banner
    chrome.actionsTopPx = rect.top - slot.top
    chrome.actionsLeftPx = rect.left - slot.left
    chrome.actionsWidthPx = rect.width
    with(density) {
        chrome.actionsTopDp = chrome.actionsTopPx.toDp().value
        chrome.actionsLeftDp = chrome.actionsLeftPx.toDp().value
        chrome.actionsWidthDp = chrome.actionsWidthPx.toDp().value
    }
}

@Composable
fun TrailerButton(
    onClick: () -> Unit,
    containerColor: Color,
    modifier: Modifier = Modifier,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
) {
    Button(
        modifier = modifier.height(48.dp),
        interactionSource = interactionSource,
        colors = ButtonDefaults.buttonColors(containerColor = containerColor),
        onClick = onClick,
    ) {
        Image(
            painter = painterResource(Res.drawable.film),
            colorFilter = ColorFilter.tint(UiKitTheme.colors.color_7),
            contentDescription = null,
        )
        Spacer(modifier = Modifier.width(6.dp))
        TextLabel(
            state = TextLabelState(
                text = stringResource(Res.string.trailer),
                textStyle = BoldMedium,
                color = UiKitTheme.colors.color_7,
            ),
        )
    }
}

@Composable
fun TrailerLayer(
    trailerUrl: String?,
    expansion: TrailerExpansionState,
    banner: Rect?,
    scrollState: ScrollState,
    pullToExpand: Boolean,
    collapsedCorner: Dp,
    onBack: () -> Unit,
    onUnavailable: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val url = trailerUrl?.takeIf { it.isNotBlank() } ?: return
    val place = banner ?: return
    if (place.width < 2f || place.height < 2f) return
    SideEffect {
        TrailerControls.onFailed = onUnavailable
    }
    DisposableEffect(Unit) {
        onDispose { TrailerControls.onFailed = null }
    }

    val density = LocalDensity.current
    val scope = rememberCoroutineScope()
    val progress = expansion.progress
    val threshold = with(density) { 88.dp.toPx() }
    var pullPx by remember { mutableFloatStateOf(0f) }
    val focusRequester = remember { FocusRequester() }
    val expanded = progress > 0.55f
    OnBackPressed(enabled = progress > 0.02f) {
        expansion.collapse()
    }
    LaunchedEffect(expanded) {
        if (expanded) {
            runCatching { focusRequester.requestFocus() }
        }
    }

    BoxWithConstraints(
        modifier
            .fillMaxSize()
            .zIndex(if (progress > 0.02f) 6f else -1f)
            .then(
                if (progress > 0.02f) {
                    Modifier.focusRequester(focusRequester).focusable()
                } else {
                    Modifier
                },
            )
            .onPreviewKeyEvent { event ->
                if (event.type == KeyEventType.KeyDown && event.key == Key.Escape && progress > 0.02f) {
                    if (expanded) expansion.collapse()
                    true
                } else {
                    false
                }
            },
    ) {
        val stageWidth = with(density) { maxWidth.toPx() }
        val stageHeight = with(density) { maxHeight.toPx() }
        val left = lerp(place.left, 0f, progress)
        val top = lerp(place.top, 0f, progress)
        val width = lerp(place.width, stageWidth, progress)
        val height = lerp(place.height, stageHeight, progress)
        val radius = collapsedCorner * (1f - progress.coerceIn(0f, 1f))

        Box(
            modifier = Modifier
                .offset { IntOffset(left.roundToInt(), top.roundToInt()) }
                .size(
                    with(density) { width.toDp() },
                    with(density) { height.toDp() },
                )
                .clip(RoundedCornerShape(radius)),
        ) {
            TrailerPlayer(
                youtubeUrl = url,
                expand = progress,
                muted = expansion.muted,
                scrim = UiKitTheme.colors.color_3,
                showBack = pullToExpand && progress < 0.2f && place.bottom > with(density) { 72.dp.toPx() },
                showClose = progress > 0.55f,
                allowPull = pullToExpand,
                cornerRadiusDp = radius.value,
                onBack = onBack,
                onClose = { expansion.collapse() },
                onExpand = { expansion.expand() },
                onVerticalDrag = { dyCss ->
                    if (progress > 0.08f) return@TrailerPlayer
                    val px = dyCss * density.density
                    val atTop = scrollState.value <= 0
                    if (pullToExpand && atTop && px > 0f) {
                        pullPx += px
                        if (pullPx >= threshold) {
                            pullPx = 0f
                            expansion.expand()
                        }
                    } else {
                        if (px < 0f) pullPx = 0f
                        scope.launch {
                            scrollState.scroll { scrollBy(-px) }
                        }
                    }
                },
                onVerticalDragEnd = { pullPx = 0f },
                modifier = Modifier.fillMaxSize(),
            )
        }
        if (pullToExpand && progress > 0.55f) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .pointerInput(threshold) {
                        var up = 0f
                        detectVerticalDragGestures(
                            onDragStart = { up = 0f },
                            onVerticalDrag = { _, dragAmount ->
                                if (dragAmount < 0f) up += -dragAmount else up = 0f
                            },
                            onDragEnd = {
                                if (up > threshold) expansion.collapse()
                                up = 0f
                            },
                            onDragCancel = { up = 0f },
                        )
                    },
            )
        }
    }
}

private fun lerp(start: Float, stop: Float, fraction: Float): Float {
    val t = fraction.coerceIn(0f, 1f)
    return start + (stop - start) * t
}
